import { useEffect, useMemo, useRef, useState } from 'react';

import { FundingKey } from '@bytel/leviator';

import { getDefaultFunding } from '@services/products';

import { MobilePlanType } from '@app-types/plan';
import { UserType } from '@app-types/user';

import { PartialMarketingsState, setReadOnlyMarketings } from '@hooks/use-marketings';

import { PlanSubTypeId } from '@constants/plan';

export function usePlanConfiguration(
    plans: MobilePlanType[],
    initialPlan: MobilePlanType | null | undefined,
    initialFundingKey: FundingKey | undefined,
    setMarketings: (marketings: PartialMarketingsState) => void,
    user: UserType | undefined,
) {
    const [selectedPlanGencode, setSelectedPlanGencode] = useState<string | undefined | null>(
        initialPlan === null ? null : initialPlan?.gencode,
    );
    const [fundingKey, setFundingKey] = useState<FundingKey | undefined>(initialFundingKey);
    const hasExplicitSelection = useRef(false);

    useEffect(() => {
        if (!hasExplicitSelection.current) {
            setSelectedPlanGencode(initialPlan === null ? null : initialPlan?.gencode);
        }
    }, [initialPlan]);

    const planSubType: PlanSubTypeId.SMART_CHANGE | PlanSubTypeId.CLASSIC = useMemo(() => {
        const currentPlan = plans.find((plan) => plan.gencode === selectedPlanGencode);
        switch (currentPlan?.subTypeId) {
            case PlanSubTypeId.SMART_CHANGE:
            case PlanSubTypeId.CLASSIC:
                return currentPlan?.subTypeId;
            default:
                return PlanSubTypeId.CLASSIC;
        }
    }, [plans, selectedPlanGencode]);

    const handleSetPlan = (newPlan: MobilePlanType | null) => {
        hasExplicitSelection.current = true;
        setSelectedPlanGencode(newPlan === null ? null : newPlan?.gencode);
        if (newPlan && user) {
            setMarketings(setReadOnlyMarketings(newPlan.details.discounts ?? []));
        }
    };

    const handleSetFunding = (newFundingKey: FundingKey) => {
        setFundingKey(newFundingKey);
    };

    const selectedPlan = useMemo(() => {
        if (selectedPlanGencode === null) {
            return null;
        }

        return plans.find((plan) => plan.gencode === selectedPlanGencode);
    }, [plans, selectedPlanGencode]);

    const selectedFunding = useMemo(() => {
        return selectedPlan ? getDefaultFunding(selectedPlan.fundings, fundingKey) : undefined;
    }, [selectedPlan, fundingKey]);

    return {
        plan: { selectedPlan, setSelectedPlan: handleSetPlan, planSubType },
        funding: { selectedFunding, setSelectedFunding: handleSetFunding },
    };
}
